import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { ConversationId, Cursor, Message, MessageId, TimelineResponse } from '@cyphex/types';
import { chatApi } from '../api';
import { CHAT_KEYS, TIMELINE_PAGE_SIZE } from '../constants';
import { flattenTimeline, type TimelinePages } from '../lib/timelinePages';

interface UseTimelineResult {
  messages: Message[];
  isLoading: boolean;
  isError: boolean;
  hasOlder: boolean;
  isFetchingOlder: boolean;
  loadOlder: () => void;
  refetch: () => void;
}

/**
 * Keyset (seq) paging, newest window first, older pages appended as the user
 * scrolls up. Never `OFFSET` — the messages table is partitioned and only the
 * `(tenant, conversation, seq DESC)` index is fast.
 *
 * `anchorId` is the permalink case: the FIRST page becomes a window centred on
 * that message (`around=`, two bounded seeks server-side) instead of the tail,
 * so opening a link to a two-year-old message costs the same one page as
 * opening the chat normally. It gets its own query key because it is a
 * genuinely different window — see `writeTimeline`, which patches both.
 */
export function useTimeline(
  conversationId: ConversationId | null,
  anchorId: MessageId | null = null,
): UseTimelineResult {
  const query = useInfiniteQuery<
    TimelineResponse,
    Error,
    TimelinePages,
    readonly unknown[],
    Cursor | undefined
  >({
    queryKey: conversationId
      ? anchorId
        ? CHAT_KEYS.timelineAround(conversationId, anchorId)
        : CHAT_KEYS.timeline(conversationId)
      : ['chat', 'timeline', 'none'],
    initialPageParam: undefined,
    enabled: Boolean(conversationId),
    queryFn: ({ pageParam }) => {
      if (!conversationId) throw new Error('No conversation selected.');
      if (!pageParam && anchorId) {
        return chatApi.getTimeline(conversationId, {
          limit: TIMELINE_PAGE_SIZE,
          around: anchorId,
        });
      }
      return chatApi.getTimeline(conversationId, {
        limit: TIMELINE_PAGE_SIZE,
        ...(pageParam ? { before: pageParam } : {}),
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMoreBefore ? (lastPage.startCursor ?? undefined) : undefined,
    staleTime: 10_000,
  });

  const messages = useMemo(() => flattenTimeline(query.data), [query.data]);

  return {
    messages,
    isLoading: query.isLoading,
    isError: query.isError,
    hasOlder: query.hasNextPage,
    isFetchingOlder: query.isFetchingNextPage,
    loadOlder: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
    refetch: () => void query.refetch(),
  };
}
